// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';

import { HoleMapCanvas } from '@/features/hole/holeview/HoleMapCanvas';
import {
  IDENTITY_VIEW,
  MIN_VIEW_ZOOM,
  type LatLon,
  type ViewTransform,
} from '@/features/hole/holeview/tileMath';
import type { TeePoint } from '@/features/hole/holeview/useHoleViewGeometry';
import { getStreetAttribution, getStreetTileUrlTemplate } from '@/features/hole/holeview/tileSource';
import { useI18n } from '@/i18n';
import appColors from '@/theme/colors';

/**
 * How much ground the map covers when it opens, corner to corner.
 *
 * A hundred kilometres, and that is a decision about which question this map answers. At this
 * span a golf course is roughly five points wide — a dot, not a course — so this is a map for
 * *where in the country is this club*, and the reader zooms in for *what does the course look
 * like*. It is also why the basemap is roads rather than imagery: at 100 km, aerial photography
 * of Swedish forest is an undifferentiated green field, while a road map shows the towns and
 * roads that make a position mean anything.
 */
export const CLUB_LOCATOR_SPAN_METERS = 100_000;

/**
 * How far in the reader may zoom.
 *
 * `MAX_VIEW_ZOOM` is 8, which is right for a view framed on one hole and useless here: 8× of
 * 100 km is still 12 km, and the course is a smudge. 128× brings the frame to about 780 m, which
 * is a golf course filling the screen. Nothing goes soft on the way — `MAX_TILE_ZOOM` caps the
 * tile level at 19, and 780 m across a phone-width view is about two metres per point, an order
 * of magnitude short of that.
 */
const CLUB_LOCATOR_MAX_ZOOM = 128;

/** Each press of the zoom buttons. Four steps from the opening frame to the course. */
const ZOOM_STEP = 4;

/**
 * Nothing is placed on this map, only ground to centre on — but a fresh `[]` on every render is
 * a changed prop to the canvas's memoised layout. A stable reference keeps a no-op render a no-op.
 */
const NO_TEES: TeePoint[] = [];

export type ClubLocatorMapProps = {
  center: LatLon;
  /** Height in points. The map is always the full width of its container. */
  height?: number;
};

/**
 * Where a golf club is, on a road map.
 *
 * Built on {@link HoleMapCanvas} rather than beside it: the tile grid, the framing and the pan
 * handling are already there and tested, and the only thing a locator needs that the hole view
 * does not is a different basemap — one optional prop. Passing no tees and no green puts the
 * canvas in the mode the hole editor already uses for a hole with nothing marked on it, which is
 * exactly this: frame the fallback ground, draw the basemap, place nothing.
 */
export function ClubLocatorMap({ center, height = 220 }: ClubLocatorMapProps) {
  const { t } = useI18n();
  const [view, setView] = useState<ViewTransform>(IDENTITY_VIEW);

  const onPan = useCallback((pan: { panX: number; panY: number }) => {
    setView((current) => ({ ...current, ...pan }));
  }, []);

  const stepZoom = (factor: number) =>
    setView((current) => {
      const zoom = Math.min(CLUB_LOCATOR_MAX_ZOOM, Math.max(MIN_VIEW_ZOOM, current.zoom * factor));
      // Zoom about the club rather than about wherever the reader has panned to. The club is the
      // subject of this map, and a zoom that walks away from it is how you end up looking at a
      // field ten kilometres from the course you asked about.
      return zoom === current.zoom ? current : { zoom, panX: 0, panY: 0 };
    });

  return (
    <View style={[styles.frame, { height }]} testID="club-locator-map">
      <HoleMapCanvas
        tees={NO_TEES}
        green={null}
        loading={false}
        unavailable={false}
        fallbackCenter={center}
        fallbackSpanMeters={CLUB_LOCATOR_SPAN_METERS}
        // Holds the 100 km frame as the thing zoom is applied to. Without it the canvas refits to
        // whatever geometry it has, which here is none.
        holdFallbackFrame
        tileUrlTemplate={getStreetTileUrlTemplate()}
        tileAttribution={getStreetAttribution()}
        view={view}
        onPan={onPan}
        minZoom={MIN_VIEW_ZOOM}
        maxZoom={CLUB_LOCATOR_MAX_ZOOM}
        canvasStyle={styles.canvas}
      />

      {/* The club is dead centre because the frame is a box built symmetrically around it, so the
          marker needs no projection of its own and cannot drift out of step with the tiles the way
          a separately-computed screen position could. Mercator makes that centring approximate
          rather than exact — a degree-symmetric box is not pixel-symmetric — but over 100 km at
          Swedish latitudes the offset is under two percent of the frame height, about a point.
          It does not move when the map is panned, which is honest: it marks the middle of the
          frame the club was framed in, and panning moves the ground. */}
      <View pointerEvents="none" style={styles.markerLayer}>
        <View style={styles.marker} testID="club-locator-marker" />
      </View>

      <View style={styles.zoomGroup}>
        <Pressable
          testID="club-locator-zoom-in"
          accessibilityRole="button"
          accessibilityLabel={t('clubPage.zoomIn')}
          disabled={view.zoom >= CLUB_LOCATOR_MAX_ZOOM}
          style={[styles.zoomButton, view.zoom >= CLUB_LOCATOR_MAX_ZOOM && styles.zoomButtonDisabled]}
          onPress={() => stepZoom(ZOOM_STEP)}
        >
          <Text style={styles.zoomLabel}>+</Text>
        </Pressable>
        <Pressable
          testID="club-locator-zoom-out"
          accessibilityRole="button"
          accessibilityLabel={t('clubPage.zoomOut')}
          disabled={view.zoom <= MIN_VIEW_ZOOM}
          style={[styles.zoomButton, view.zoom <= MIN_VIEW_ZOOM && styles.zoomButtonDisabled]}
          onPress={() => stepZoom(1 / ZOOM_STEP)}
        >
          <Text style={styles.zoomLabel}>−</Text>
        </Pressable>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  frame: {
    width: '100%',
    borderRadius: 10,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.surface.card,
  },
  canvas: {
    flex: 1,
  },
  markerLayer: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
  },
  marker: {
    width: 14,
    height: 14,
    borderRadius: 7,
    backgroundColor: appColors.accent.primary,
    borderWidth: 2,
    // A ring over map tiles, so it stays white on every skin: onMedia, not neutral.white.
    borderColor: appColors.text.onMedia,
  },
  zoomGroup: {
    position: 'absolute',
    top: 8,
    right: 8,
    gap: 6,
  },
  zoomButton: {
    width: 32,
    height: 32,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: appColors.surface.elevated,
    borderWidth: 1,
    borderColor: appColors.border.strong,
  },
  zoomButtonDisabled: {
    opacity: 0.4,
  },
  zoomLabel: {
    fontSize: 18,
    fontWeight: '700',
    color: appColors.text.primary,
  },
});
